import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { DenounceAccessService } from '../../../services/denounce-access.service';
import { DenounceOperator, DenounceRole } from '../../../interfaces/denounce.interface';

@Component({
  selector: 'app-denounce-access-manager',
  templateUrl: './denounce-access-manager.component.html',
  styleUrls: ['./denounce-access-manager.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DenounceAccessManagerComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  loading = false;
  dataSource = new MatTableDataSource<DenounceOperator>([]);
  displayedColumns = ['operatorName', 'operatorEmail', 'role', 'isActive', 'actions'];
  roles: DenounceRole[] = ['ADMIN', 'INVESTIGADOR', 'VISUALIZADOR'];

  constructor(
    private accessService: DenounceAccessService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.accessService.loading$.subscribe((l) => {
        this.loading = l;
        this.cdr.markForCheck();
      }),
    );
    this.loadOperators();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadOperators(): void {
    this.accessService.listOperators().subscribe({
      next: (ops) => {
        this.dataSource.data = ops;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.snackBar.open(err.message || 'Error al cargar operadores', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  async addOperator(): Promise<void> {
    const { value: formValues } = await Swal.fire({
      title: 'Agregar operador',
      html:
        '<input id="swal-email" class="swal2-input" type="email" placeholder="Email del operador">' +
        '<select id="swal-role" class="swal2-select">' +
        '<option value="VISUALIZADOR">Visualizador</option>' +
        '<option value="INVESTIGADOR">Investigador</option>' +
        '<option value="ADMIN">Admin</option>' +
        '</select>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Agregar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1565C0',
      preConfirm: () => {
        const email = (document.getElementById('swal-email') as HTMLInputElement).value?.trim();
        const role = (document.getElementById('swal-role') as HTMLSelectElement).value;
        if (!email) {
          Swal.showValidationMessage('El email del operador es requerido');
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          Swal.showValidationMessage('Ingrese un email válido');
          return;
        }
        return { email, role };
      },
    });

    if (!formValues) return;

    this.accessService
      .addOperator(formValues as any)
      .subscribe({
        next: () => {
          this.snackBar.open('Operador agregado', 'Cerrar', { duration: 3000 });
          this.loadOperators();
        },
        error: (err) => {
          this.snackBar.open(err.message || 'Error al agregar operador', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  changeRole(operator: DenounceOperator, newRole: DenounceRole): void {
    this.accessService.updateRole(operator.id, newRole).subscribe({
      next: () => {
        this.snackBar.open('Rol actualizado', 'Cerrar', { duration: 3000 });
        this.loadOperators();
      },
      error: (err) => {
        this.snackBar.open(err.message || 'Error al cambiar rol', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  async revokeAccess(operator: DenounceOperator): Promise<void> {
    const result = await Swal.fire({
      title: '¿Revocar acceso?',
      text: `Se eliminará el acceso de ${operator.operatorName} al módulo de denuncias.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Revocar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    });

    if (!result.isConfirmed) return;

    this.accessService.revokeAccess(operator.id).subscribe({
      next: () => {
        this.snackBar.open('Acceso revocado', 'Cerrar', { duration: 3000 });
        this.loadOperators();
      },
      error: (err) => {
        this.snackBar.open(err.message || 'Error al revocar', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }
}
